import BroadcastChannel from 'broadcast-channel';
import PropTypes from 'prop-types';
import React from 'react';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { makeListCacheUpdater } from '../../../common/apolloHelpers';
import { extractErrorMessage } from '../../../common/components/utils/Error';
import withTranslations from '../../../common/components/utils/Translations/withTranslations';
import withCurrentUser from '../../../common/data/withCurrentUser';

import currentUserGql from '../../../common/data/withCurrentUser.graphql';
import { handleFormErrors } from '../../../common/errors';
import propTypes from '../../../common/propTypes';

import translatePropTypes from '../../../common/translatePropTypes';
import Notifications from '../../../common/utils/Notifications/Notifications';
import SocialLoginButton from '../../../common/utils/SocialLoginButton';
import SocialLoginButtonView from '../../../common/utils/SocialLoginButton/SocialLoginButtonView';
import RestrictedOperationConfirmation from '../RestrictedOperationConfirmation';
import continueLinkingSocialAccountGql from './continueLinkingSocialAccount.graphql';
import linkSocialAccountGql from './linkSocialAccount.graphql';
import unlinkSocialAccountGql from './unlinkSocialAccount.graphql';

@withRouter
@withTranslations
@withCurrentUser({ inline: false })
@graphql(linkSocialAccountGql, {
  name: 'link',
  options: {
    update: makeListCacheUpdater(currentUserGql, {
      destinationListPath: 'me.socialAccounts',
      sourceItemPath: 'linkSocialAccount.socialAccount',
      skip: socialAccount => !socialAccount,
    }),
  },
})
@graphql(unlinkSocialAccountGql, {
  name: 'unlink',
  options: {
    update: makeListCacheUpdater(currentUserGql, {
      destinationListPath: 'me.socialAccounts',
    }),
  },
})
@graphql(continueLinkingSocialAccountGql, {
  name: 'continueLinking',
  options: {
    update: makeListCacheUpdater(currentUserGql, {
      destinationListPath: 'me.socialAccounts',
      sourceItemPath: 'continueLinkingSocialAccount.socialAccount',
      skip: socialAccount => !socialAccount,
    }),
  },
})
@graphql(currentUserGql, {
  name: 'getCurrentUser',
  options: ({ id }) => ({
    variables: {
      id,
    },
  }),
})
export default class SocialAccountPageContent extends React.Component {
  static propTypes = {
    ...translatePropTypes,
    type: propTypes.socialAccountType.isRequired,
    afterCompletionPath: PropTypes.string,
    ...withCurrentUser.props,
    ...propTypes.routePropTypes,
  };

  static defaultProps = {
    afterCompletionPath: null,
    ...withCurrentUser.defaultProps,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: !!props.afterCompletionPath,
      error: null,
      status: null,
      loginChannel: null,
    };
  }

  componentDidMount = () => {
    const bc = new BroadcastChannel(
      SocialAccountPageContent.LINK_SOCIAL_ACCOUNT_BROADCAST_CHANNEL,
    );

    bc.addEventListener(
      SocialAccountPageContent.LISTENER_TYPE,
      this.loginEventListener,
    );

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      loading: false,
      loginChannel: bc,
    });
  };

  componentDidUpdate() {
    const { history, afterCompletionPath } = this.props;
    const { error } = this.state;
    const account = this.findAccount();

    if (afterCompletionPath && (account || error)) {
      history.replace(afterCompletionPath);
    }
  }

  componentWillUnmount() {
    this.state.loginChannel.removeEventListener(
      SocialAccountPageContent.LISTENER_TYPE,
      this.loginEventListener,
    );
  }

  static LINK_SOCIAL_ACCOUNT_BROADCAST_CHANNEL = 'linkSocialAccount';
  static LISTENER_TYPE = 'message';

  loginEventListener(e) {
    const { loading } = this.state || {};
    if ((e === 'microsoft-account' || 'google-account') && !loading) {
      window.location = `/profile/account-settings/${e}`;
    }
  }

  showError = error => {
    const { t } = this.props;
    Notifications.error(
      t('Error'),
      extractErrorMessage(error, t) ||
        t(
          'You have already unlinked the account. Please try reloading the page to see the changes.',
        ),
      t,
    );
  };

  findAccount() {
    const { type, currentUser } = this.props;
    return (
      currentUser && currentUser.socialAccounts.find(acc => acc.type === type)
    );
  }

  decidePostMessage = type => {
    if (type === 'MICROSOFT')
      this.state.loginChannel.postMessage('microsoft-account');
    else this.state.loginChannel.postMessage('google-account');
  };

  handleLinkAccount = async token => {
    const { link, type } = this.props;

    try {
      this.setState({ loading: true });

      const context = {};
      switch (type) {
        case 'GOOGLE':
          context.googleIdToken = token;
          break;
        case 'MICROSOFT':
          context.bearerToken = token;
          break;
        default:
          break;
      }
      const { data } = await link({ context, variables: { type } });
      this.decidePostMessage(type);
      this.setState({ loading: false, status: data.linkSocialAccount });
    } catch (error) {
      this.showError(error);
      this.setState({
        loading: false,
        error,
      });
    }
  };

  handleUnlinkAccount = async e => {
    e.preventDefault();

    const { unlink, type } = this.props;
    const account = this.findAccount();

    try {
      this.setState({ loading: true });
      await handleFormErrors(
        unlink({
          variables: { id: account.id },
        }),
      );
      this.decidePostMessage(type);
      this.setState({ loading: false });
    } catch (error) {
      this.showError(error);
      this.setState({ loading: false, error });
    }
  };

  handleLoginError = error => {
    this.showError(error);
    this.setState({ error, loading: false });
  };

  handleConfirmation = async ({ emailConfirmationToken }) => {
    const { continueLinking } = this.props;

    const { data } = await handleFormErrors(
      continueLinking({
        variables: { emailConfirmationToken },
      }),
    );

    this.setState({ status: data.continueLinkingSocialAccount });
    return data.continueLinkingSocialAccount;
  };

  handleCancelConfirmation = () => {
    const { afterCompletionPath, history, match } = this.props;
    this.setState({ status: null });
    if (afterCompletionPath) {
      history.replace(afterCompletionPath);
    } else {
      history.replace(match.url);
    }
  };

  handleLoginInitiated = () => this.setState({ loading: true });

  render() {
    const { t, formatShortDate, type } = this.props;
    const { loading, status } = this.state;
    const networkName = SocialLoginButtonView.networkName(type);
    const account = this.findAccount();

    if (!account) {
      return (
        <>
          <h6>
            {t(
              'You have not linked your #{name} account to your Edana account. Click on button below to link...',
              {
                name: t(networkName),
              },
            )}
          </h6>
          <SocialLoginButton
            completionPath="/profile/account-settings/microsoft-account/complete"
            disabled={loading}
            loading={loading}
            titleTemplate={t('Link #{name} Account', {
              name: t(networkName),
            })}
            type={type}
            onLogin={this.handleLinkAccount}
            onLoginError={this.handleLoginError}
            onLoginInitiated={this.handleLoginInitiated}
          />
          <RestrictedOperationConfirmation
            operationStatus={status}
            updateMessageText="Account Linked Successfully"
            onCancel={this.handleCancelConfirmation}
            onConfirm={this.handleConfirmation}
          />
        </>
      );
    }

    return (
      <>
        <h6>
          {t('You have linked your #{name} account on #{date}', {
            name: networkName,
            date: formatShortDate(account.linkedAt),
          })}
        </h6>
        <p>
          <strong>{account.accountIdentifier}</strong>
          <a
            className="text-danger ml-20"
            href="#"
            onClick={this.handleUnlinkAccount}
          >
            {t('Unlink')}
          </a>
        </p>
      </>
    );
  }
};
